import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faImages, faUpload } from '@fortawesome/free-solid-svg-icons';
import { customSwal } from '../../CustomSwal';
import ConsoleMediaLibrary from '../ConsoleMediaLibrary';
import { ACCEPTED_IMAGE_TYPES, uploadConsoleImage } from '../consoleMediaApi';

const buttonClass = 'inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-white';

const assetToImage = (asset) => ({
  url: asset.url,
  publicId: asset.publicId,
  width: asset.width,
  height: asset.height,
});

export const ImageEditor = ({ draft, onChange, consoleId }) => {
  const token = useSelector((state) => state.userLogin.userInfo?.token);
  const [uploading, setUploading] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const image = draft.image;

  // Swapping the picture keeps any alt text already typed
  const setImage = (changes) => onChange((prev) => ({ image: { ...(prev.image || {}), ...changes } }));

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    setUploading(true);
    try {
      setImage(assetToImage(await uploadConsoleImage(file, { consoleId }, token)));
    } catch (error) {
      customSwal.fire({ icon: 'error', title: 'Image upload failed', text: error.message });
    } finally {
      setUploading(false);
    }
  };

  const actions = (
    <div className="flex flex-wrap justify-center gap-2">
      <label
        className={`${buttonClass} ${uploading ? 'cursor-wait bg-blue-600/50' : 'cursor-pointer bg-blue-600 hover:bg-blue-700'}`}
      >
        <FontAwesomeIcon icon={faUpload} />
        {uploading ? 'Uploading…' : image?.url ? 'Upload new' : 'Upload image'}
        <input type="file" accept={ACCEPTED_IMAGE_TYPES} hidden disabled={uploading} onChange={handleUpload} />
      </label>
      <button
        type="button"
        onClick={() => setLibraryOpen(true)}
        className={`${buttonClass} bg-gray-700 hover:bg-gray-600`}
      >
        <FontAwesomeIcon icon={faImages} />
        Choose from library
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      <div>
        <label className="block mb-2 font-medium">Image:</label>
        {image?.url ? (
          <div className="overflow-hidden rounded-lg border border-gray-600 bg-gray-900">
            <img src={image.url} alt={image.alt || ''} className="mx-auto max-h-80 w-auto object-contain" />
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-700 px-3 py-2">
              <span className="text-xs text-gray-400">
                {image.width && image.height ? `${image.width} × ${image.height}px` : 'Image'}
              </span>
              {actions}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border-2 border-dashed border-gray-600 px-6 py-10 text-center">
            <FontAwesomeIcon icon={faImage} className="mb-2 text-3xl text-gray-500" />
            <p className="mb-4 text-sm text-gray-300">Upload an image, or pick one you've already uploaded.</p>
            {actions}
          </div>
        )}
      </div>

      <div>
        <label className="block mb-1 font-medium">Alt text:</label>
        <p className="mb-2 text-xs text-gray-400">
          Describes the image for screen readers and search engines.
        </p>
        <input
          type="text"
          className="w-full p-2 rounded bg-gray-700 text-white disabled:opacity-50"
          value={image?.alt || ''}
          onChange={(e) => setImage({ alt: e.target.value })}
          disabled={!image?.url}
          placeholder="e.g. Instructor leading a small group class"
        />
      </div>

      <ConsoleMediaLibrary
        isOpen={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        consoleId={consoleId}
        onSelect={(asset) => {
          setImage(assetToImage(asset));
          setLibraryOpen(false);
        }}
      />
    </div>
  );
};

export const ImageViewer = ({ item }) => {
  const image = item.image;
  if (!image?.url) {
    return <p className="italic text-gray-400">This item has no image yet.</p>;
  }

  return (
    <figure className="space-y-3">
      <div className="overflow-hidden rounded-lg border border-gray-700 bg-black/40">
        <img src={image.url} alt={image.alt || ''} className="mx-auto max-h-[28rem] w-auto object-contain" />
      </div>
      <figcaption className="space-y-1 text-sm text-gray-300">
        <p>
          <span className="text-gray-400">Alt text: </span>
          {image.alt || <em className="text-yellow-200">none yet; add some for accessibility</em>}
        </p>
        {image.width && image.height && (
          <p className="text-gray-400">{image.width} × {image.height}px</p>
        )}
        <a
          href={image.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block text-blue-300 underline hover:text-blue-200"
        >
          Open full size
        </a>
      </figcaption>
    </figure>
  );
};
